import { useEffect, useId, useRef, useState } from 'react';
import { ROUTES, enquirySchema, fieldErrors, type EnquiryField } from '../lib/contact.ts';

export const INBOX = 'hub@synapsefleet.eu';

type Values = Record<'route' | 'name' | 'email' | 'company' | 'message', string>;
const EMPTY: Values = { route: '', name: '', email: '', company: '', message: '' };

type Status = 'idle' | 'sending' | 'sent' | 'failed';

export function ContactForm() {
  const id = useId();
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<EnquiryField, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [failure, setFailure] = useState('');
  const form = useRef<HTMLFormElement>(null);
  const trap = useRef<HTMLInputElement>(null);
  const moveFocus = useRef(false);
  const done = useRef<HTMLHeadingElement>(null);

  // Focus has to wait for the render that paints the errors. Reading the DOM
  // straight after setErrors finds the previous render, which is why the first
  // version of this quietly focused nothing at all.
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [errors]);

  // The form unmounts on success, taking the focused button with it. Without
  // this, focus falls to <body>, the next Tab restarts at the skip link, and a
  // screen reader says nothing: a live region that arrives already containing
  // its text is not a change anything was watching for.
  useEffect(() => {
    if (status === 'sent') done.current?.focus();
  }, [status]);

  const set = (field: keyof Values) => (value: string) => {
    setValues((was) => ({ ...was, [field]: value }));
    // Clear an error only once it is showing, so nothing turns amber while a
    // field is still being typed for the first time.
    if (errors[field as EnquiryField]) {
      setErrors((was) => ({ ...was, [field]: undefined }));
    }
  };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'sending') return;

    const payload = { ...values, company_url: trap.current?.value ?? '' };
    const parsed = enquirySchema.safeParse(payload);
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      moveFocus.current = true;
      // Otherwise a retry that fails validation keeps the previous network
      // error under the button, blaming the connection for a typo.
      setStatus('idle');
      setFailure('');
      return;
    }

    setErrors({});
    setStatus('sending');
    setFailure('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
        // The button must never sit on "Sending…" indefinitely, whatever the
        // server is doing or not doing.
        signal: AbortSignal.timeout(20_000),
      });
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        fields?: Partial<Record<EnquiryField, string>>;
      };

      if (!response.ok) {
        // The server revalidates, and can disagree. Show it against the fields.
        if (body.fields) {
          setErrors(body.fields);
          moveFocus.current = true;
          setStatus('idle');
          return;
        }
        throw new Error(body.error ?? 'Something went wrong.');
      }

      setValues(EMPTY);
      setStatus('sent');
    } catch (error) {
      const timedOut = error instanceof DOMException && error.name === 'TimeoutError';
      setFailure(
        timedOut
          ? 'That took too long and we stopped waiting.'
          : error instanceof Error
            ? error.message
            : 'Something went wrong.',
      );
      setStatus('failed');
    }
  }

  if (status === 'sent') {
    return (
      <div className="form__done">
        <h2 ref={done} tabIndex={-1}>That is with us.</h2>
        <p>
          We have sent you a copy of what you wrote. If you would rather chase it directly, we are
          at <a href={`mailto:${INBOX}`}>{INBOX}</a>.
        </p>
      </div>
    );
  }

  const field = (name: EnquiryField) => ({
    id: `${id}-${name}`,
    name,
    value: values[name as keyof Values],
    onChange: (event: { target: { value: string } }) => set(name as keyof Values)(event.target.value),
    'aria-invalid': errors[name] ? (true as const) : undefined,
    'aria-describedby': errors[name] ? `${id}-${name}-err` : undefined,
  });

  return (
    <form className="form" ref={form} onSubmit={submit} noValidate>
      <fieldset className="form__routes">
        <legend>Which of these are you?</legend>
        <div className="form__pills">
          {ROUTES.map((route) => (
            <label className="form__pill" key={route.id}>
              <input
                type="radio"
                required
                name="route"
                value={route.id}
                checked={values.route === route.id}
                onChange={() => set('route')(route.id)}
                aria-invalid={errors.route ? true : undefined}
                aria-describedby={errors.route ? `${id}-route-err` : undefined}
              />
              <span>{route.label}</span>
            </label>
          ))}
        </div>
        {errors.route && <p className="form__err" id={`${id}-route-err`}>{errors.route}</p>}
      </fieldset>

      <div className="form__row">
        <div className="form__field">
          <label htmlFor={`${id}-name`}>Name</label>
          <input type="text" required autoComplete="name" {...field('name')} />
          {errors.name && <p className="form__err" id={`${id}-name-err`}>{errors.name}</p>}
        </div>

        <div className="form__field">
          <label htmlFor={`${id}-email`}>Work email</label>
          <input type="email" required autoComplete="email" {...field('email')} />
          {errors.email && <p className="form__err" id={`${id}-email-err`}>{errors.email}</p>}
        </div>
      </div>

      <div className="form__field">
        <label htmlFor={`${id}-company`}>
          Company <span className="form__opt">optional</span>
        </label>
        <input type="text" autoComplete="organization" {...field('company')} />
      </div>

      <div className="form__field">
        <label htmlFor={`${id}-message`}>What is on your floor?</label>
        <textarea rows={5} required {...field('message')} />
        {errors.message && <p className="form__err" id={`${id}-message-err`}>{errors.message}</p>}
      </div>

      {/* Off-screen rather than display:none — some bots skip anything a
          browser would not paint, and this one is meant to be filled in. */}
      <input className="form__trap" ref={trap} type="text" name="company_url" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="form__foot">
        <button className="duo__btn" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Send it'}
          <span aria-hidden="true">↗</span>
        </button>
        {/* Mounted at all times, so a message arriving in it is announced. A
            live region added to the DOM alongside its text announces nothing. */}
        <p className="form__note" role="status">
          {status === 'failed' ? (
            <>
              {failure} You can also write to <a href={`mailto:${INBOX}`}>{INBOX}</a>.
            </>
          ) : (
            'Goes to one inbox, read by the people who would run the assessment.'
          )}
        </p>
      </div>
    </form>
  );
}
